import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Hotel, Shield } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import PhoneInput from "@/components/PhoneInput";
import { isNativeApp } from "@/lib/nativePush";
import NativeAuthShell from "@/components/NativeAuthShell";

export default function HotelRegister() {
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [form, setForm] = useState({
    hotel_name: "",
    hotel_address: "",
    manager_name: "",
    phone: "",
    contact_email: "",
    booking_frequency: "occasional",
    password: "",
    password_confirm: "",
    region: "paca",
  });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.booking_frequency) {
      toast.error("Veuillez sélectionner une fréquence de réservation");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Le mot de passe doit faire au moins 6 caractères");
      return;
    }
    if (form.password !== form.password_confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      const { password_confirm, ...payload } = form;
      void password_confirm;
      const { data } = await api.post("/auth/register-hotel", payload);
      await setSession(data);
      toast.success("Compte créé — bienvenue sur Saor !");
      nav("/dashboard/hotel", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const formNode = (
    <form onSubmit={submit} className="space-y-5" data-testid="hotel-register-form">
      <Field label="Nom de l'hôtel" required>
        <Input value={form.hotel_name} onChange={upd("hotel_name")} required data-testid="hotel-name" placeholder="Hôtel des Lumières" />
      </Field>
      <Field label="Adresse de l'hôtel" required>
        <AddressAutocomplete
          value={form.hotel_address}
          onChange={(v) => setForm((f) => ({ ...f, hotel_address: v }))}
          required
          region={form.region}
          data-testid="hotel-address"
          placeholder={form.region === "idf" ? "37 Rue de Rivoli, 75001 Paris" : "37 Promenade des Anglais, 06000 Nice"}
        />
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Nom du responsable" required>
          <Input value={form.manager_name} onChange={upd("manager_name")} required data-testid="manager-name" placeholder="Claire Dupont" />
        </Field>
        <Field label="Numéro de téléphone" required>
          <PhoneInput value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} data-testid="phone" />
        </Field>
      </div>
      <Field label="Adresse mail de contact" required>
        <Input type="email" name="email" autoComplete="email" value={form.contact_email} onChange={upd("contact_email")} required data-testid="contact-email" placeholder="contact@monhotel.com" />
      </Field>
      <Field label="Fréquence moyenne de réservations / jour" required>
        <Select value={form.booking_frequency} onValueChange={upd("booking_frequency")}>
          <SelectTrigger data-testid="booking-frequency"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1-5">1 à 5 courses / jour</SelectItem>
            <SelectItem value="6-15">6 à 15 courses / jour</SelectItem>
            <SelectItem value="16-30">16 à 30 courses / jour</SelectItem>
            <SelectItem value="30+">Plus de 30 courses / jour</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Zone d'activité" required>
        <Select value={form.region} onValueChange={upd("region")}>
          <SelectTrigger data-testid="region"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="paca">Région PACA (Côte d&apos;Azur)</SelectItem>
            <SelectItem value="idf">Île-de-France (Paris)</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Mot de passe" required>
        <Input type="password" name="new-password" autoComplete="new-password" value={form.password} onChange={upd("password")} minLength={6} required data-testid="password" placeholder="Minimum 6 caractères" />
      </Field>
      <Field label="Confirmer le mot de passe" required>
        <Input type="password" name="new-password-confirm" autoComplete="new-password" value={form.password_confirm} onChange={upd("password_confirm")} minLength={6} required data-testid="password-confirm" placeholder="Retapez votre mot de passe" />
      </Field>

      <div className="pt-4">
        <Button type="submit" size="lg" disabled={loading} className="w-full rounded-full bg-primary hover:bg-primary/90 sm:w-auto sm:px-10" data-testid="hotel-submit">
          {loading ? "Création..." : "Créer mon compte hôtel"}
        </Button>
      </div>
    </form>
  );

  if (isNativeApp()) {
    return (
      <NativeAuthShell
        title="Inscrire votre hôtel"
        subtitle="Aucun engagement. Accès immédiat après inscription."
      >
        {formNode}
      </NativeAuthShell>
    );
  }

  return (
    <div className="mx-auto grid max-w-[1400px] gap-10 px-4 py-12 sm:px-6 sm:py-16 md:grid-cols-12 lg:px-10">
      <aside className="md:col-span-5">
        <div className="sticky top-24 rounded-2xl border border-border bg-card p-8 shadow-sm">
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Hotel className="h-5 w-5" />
          </div>
          <h1 className="mt-5 font-[Outfit] text-3xl font-normal tracking-tight sm:text-4xl">Inscrire votre hôtel</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Créez votre compte hôtel Saor pour réserver un chauffeur premium en quelques secondes.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            <li className="flex items-start gap-2"><Shield className="mt-0.5 h-4 w-4 text-primary" /> Aucun engagement, aucune carte requise.</li>
            <li className="flex items-start gap-2"><Shield className="mt-0.5 h-4 w-4 text-primary" /> Accès immédiat après inscription.</li>
            <li className="flex items-start gap-2"><Shield className="mt-0.5 h-4 w-4 text-primary" /> L'adresse de l'hôtel est pré-remplie lors de chaque réservation.</li>
          </ul>
        </div>
      </aside>

      <div className="md:col-span-7">{formNode}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm">{label} {required && <span className="text-accent">*</span>}</Label>
      {children}
    </div>
  );
}
